import * as React from 'react'

import { aggregateSkillsData, skillGrowthTooltipText, postSkillScoreTooltipText, preSkillScoreAllStudentsWithPreTooltipText, preSkillScoreStudentsWithPreAndPostTooltipText, studentsImprovedSkillTooltipText, studentsMaintainedSkillTooltipText, studentsImprovedOrMaintainedSkillTooltipText, studentsWithoutImprovementTooltipText } from './helpers'

import { Spinner, DataTable, noResultsMessage, DropdownInput } from '../../../Shared/index'
import useScaledTableColumnWidths from '../../../Shared/hooks/useScaledTableColumnWidths'
import { DropdownObjectInterface } from '../../../Staff/interfaces/evidenceInterfaces'
import { getDiagnosticTypeDropdownOptions, groupByDropdownOptions, hashPayload } from '../../shared'
import { requestPost } from '../../../../modules/request'

const QUERY_KEY = "diagnostic-skills"
const PUSHER_EVENT_KEY = "admin-diagnostic-skills-cached";
const SKILL_GROWTH = 'Skill Growth'

// Column widths for scaling
const COLUMN_WIDTHS = [
  { width: 150 },  // Skill
  { width: 174 },  // Pre Skill Score (All Students W/ Pre)
  { width: 200 },  // Pre Skill Score (Students W/ Pre and Post)
  { width: 200 },  // Post Skill Score
  { width: 174 },  // Skill Growth
  { width: 160 },  // Students Improved Skill
  { width: 192 },  // Students Maintained Skill
  { width: 192 },  // Students Improved Or Maintained Skill
  { width: 184 },  // Students Without Improvement
]

const MIN_TABLE_WIDTH = COLUMN_WIDTHS.reduce((sum, col) => sum + col.width, 0)
const MAX_TABLE_WIDTH = 1834

const getHeaders = (scaledWidths) => [
  {
    name: 'Skill',
    attribute: 'name',
    width: scaledWidths['150w'],
    noTooltip: true,
    isSortable: true,
    renderCellsAsHeaders: true
  },
  {
    name: '',
    ariaLabel: 'Pre Skill Score (All Students W/ Pre)',
    attribute: 'preSkillScoreAllStudentsWithPre',
    sortAttribute: 'pre_score',
    width: scaledWidths['174w'],
    primaryTitle: 'Pre Skill Score',
    secondaryTitle: '(All Students W/ Pre)',
    tooltipName: 'Pre Skill Score (All Students W/ Pre)',
    tooltipDescription: preSkillScoreAllStudentsWithPreTooltipText,
    noTooltip: true,
    isSortable: true
  },
  {
    name: '',
    ariaLabel: 'Pre Skill Score (Students W/ Pre and Post)',
    attribute: 'preSkillScoreStudentsWithPreAndPost',
    sortAttribute: 'pre_score',
    width: scaledWidths['200w'],
    primaryTitle: 'Pre Skill Score',
    secondaryTitle: '(Students W/ Pre and Post)',
    tooltipName: 'Pre Skill Score (Students W/ Pre and Post)',
    tooltipDescription: preSkillScoreStudentsWithPreAndPostTooltipText,
    noTooltip: true,
    isSortable: true
  },
  {
    name: '',
    ariaLabel: 'Post Skill Score (Students W/ Pre and Post)',
    attribute: 'postSkillScore',
    sortAttribute: 'post_score',
    width: scaledWidths['200w'],
    primaryTitle: 'Post Skill Score',
    secondaryTitle: '(Students W/ Pre and Post)',
    tooltipName: 'Post Skill Score (Students W/ Pre and Post)',
    tooltipDescription: postSkillScoreTooltipText,
    noTooltip: true,
    isSortable: true
  },
  {
    name: SKILL_GROWTH,
    attribute: 'skillGrowth',
    sortAttribute: 'skillGrowthSortValue',
    width: scaledWidths['174w'],
    tooltipName: SKILL_GROWTH,
    tooltipDescription: skillGrowthTooltipText,
    noTooltip: true,
    isSortable: true
  },
  {
    name: '',
    ariaLabel: 'Students Improved Skill',
    attribute: 'studentsImprovedSkill',
    sortAttribute: 'improved_proficiency',
    width: scaledWidths['160w'],
    primaryTitle: 'Students',
    secondaryTitle: 'Improved Skill',
    tooltipName: 'Students Improved Skill',
    tooltipDescription: studentsImprovedSkillTooltipText,
    noTooltip: true,
    isSortable: true
  },
  {
    name: '',
    ariaLabel: 'Students Maintained Skill',
    attribute: 'studentsMaintainedSkill',
    sortAttribute: 'maintained_proficiency',
    width: scaledWidths['192w'],
    primaryTitle: 'Students Maintained',
    secondaryTitle: 'Skill',
    tooltipName: 'Students Maintained Skill',
    tooltipDescription: studentsMaintainedSkillTooltipText,
    noTooltip: true,
    isSortable: true
  },
  {
    name: '',
    ariaLabel: 'Students Improved Or Maintained Skill',
    attribute: 'studentsImprovedOrMaintainedSkill',
    sortAttribute: 'studentsImprovedOrMaintainedSortValue',
    width: scaledWidths['192w'],
    primaryTitle: 'Students Improved',
    secondaryTitle: 'Or Maintained Skill',
    tooltipName: 'Students Improved Or Maintained Skill',
    tooltipDescription: studentsImprovedOrMaintainedSkillTooltipText,
    noTooltip: true,
    isSortable: true
  },
  {
    name: '',
    ariaLabel: 'Students Without Improvement',
    attribute: 'studentsWithoutImprovement',
    sortAttribute: 'recommended_practice',
    width: scaledWidths['184w'],
    primaryTitle: 'Students Without',
    secondaryTitle: 'Improvement',
    tooltipName: 'Students Without Improvement',
    tooltipDescription: studentsWithoutImprovementTooltipText,
    noTooltip: true,
    isSortable: true
  },
]


export const SkillSection = ({
  searchCount,
  selectedGrades,
  selectedSchoolIds,
  selectedTeacherIds,
  selectedClassroomIds,
  selectedTimeframe,
  pusherChannel,
  diagnosticTypeValue,
  groupByValue,
  handleSetDiagnosticIdForStudentCount,
  handleSetSelectedDiagnosticType,
  handleSetSelectedGroupByValue,
  handleNavigateToSkillStatusPerStudent,
  passedData,
  horizontalFilters,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [pusherMessage, setPusherMessage] = React.useState<string>(null)

  const scaledWidths = useScaledTableColumnWidths({
    columnWidths: COLUMN_WIDTHS,
    containerRef,
    maxTableWidth: MAX_TABLE_WIDTH,
    minTableWidth: MIN_TABLE_WIDTH,
  })

  const headers = getHeaders(scaledWidths)
  const [skillsData, setSkillsData] = React.useState<any>(null);
  const [aggregatedData, setAggregatedData] = React.useState<any>(passedData || []);
  const [loading, setLoading] = React.useState<boolean>(!passedData);

  React.useEffect(() => {
    handleSetDiagnosticIdForStudentCount(null)
  }, [])

  React.useEffect(() => {
    initializePusher()
  }, [pusherChannel])

  React.useEffect(() => {
    if (!passedData && groupByValue && diagnosticTypeValue) {
      // this is for testing purposes; this value will always be null in a non-testing environment

      // If the timeframe has changed, we may be re-populating the selectedDiagnosticType drop-downs
      // In these cases, we need to re-select the appropriate value from the drop-down for the new timeframe
      // The re-selection will re-trigger this effect by changing the value of diagnosticTypeValue
      const diagnosticTypeDropdownOptions = getDiagnosticTypeDropdownOptions(selectedTimeframe)
      if (!diagnosticTypeDropdownOptions.includes(diagnosticTypeValue)) {
        const selectedDiagnosticType = diagnosticTypeDropdownOptions.find((option) => option.label === diagnosticTypeValue.label)

        handleSetSelectedDiagnosticType(selectedDiagnosticType)
      } else {
        getData()
      }
    }
  }, [searchCount, groupByValue, diagnosticTypeValue])

  React.useEffect(() => {
    if (!pusherMessage) return

    if (filtersMatchHash(pusherMessage)) getData()
  }, [pusherMessage])

  React.useEffect(() => {
    if (skillsData) {
      aggregateSkillsData({
        skillsData,
        setAggregatedData,
        setLoading,
        handleNavigateToSkillStatusPerStudent
      })
    }
  }, [skillsData])

  function initializePusher() {
    pusherChannel?.bind(PUSHER_EVENT_KEY, (body) => {
      const { message, } = body

      setPusherMessage(message)
    });
  };

  function getData() {
    setLoading(true)
    const searchParams = {
      query: QUERY_KEY,
      timeframe: selectedTimeframe,
      school_ids: selectedSchoolIds,
      teacher_ids: selectedTeacherIds,
      classroom_ids: selectedClassroomIds,
      grades: selectedGrades,
      group_by: groupByValue.value,
      diagnostic_id: diagnosticTypeValue.value
    }

    requestPost('/admin_diagnostic_skills/report', searchParams, (body) => {
      if (!body.hasOwnProperty('results')) { return }
      const { results, } = body
      setSkillsData(results)
    })
  }

  function filtersMatchHash(hashMessage) {
    const filterTarget = [].concat(
      QUERY_KEY,
      groupByValue.value,
      parseInt(diagnosticTypeValue.value),
      selectedTimeframe,
      selectedSchoolIds,
      selectedGrades,
      selectedTeacherIds,
      selectedClassroomIds,
    )

    const filterHash = hashPayload(filterTarget)

    return hashMessage == filterHash
  }

  function handleDiagnosticTypeOptionChange(option: DropdownObjectInterface) {
    handleSetSelectedDiagnosticType(option)
  }

  function handleGroupByOptionChange(option: DropdownObjectInterface) {
    handleSetSelectedGroupByValue(option)
  }

  function renderContent() {
    if(loading) {
      return <Spinner />
    }
    return(
      <div ref={containerRef}>
        <DataTable
          className="growth-diagnostic-reports-by-skill-table"
          emptyStateMessage={noResultsMessage('diagnostic')}
          headers={headers}
          reportingFormat={true}
          rows={aggregatedData}
        />
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="dropdowns-container">
        <DropdownInput
          className="diagnostic-type-dropdown bordered small focus-on-light"
          handleChange={handleDiagnosticTypeOptionChange}
          isSearchable={true}
          label="Diagnostic:"
          options={getDiagnosticTypeDropdownOptions(selectedTimeframe)}
          value={diagnosticTypeValue}
        />
        <DropdownInput
          className="group-by-dropdown bordered small focus-on-light"
          handleChange={handleGroupByOptionChange}
          isSearchable={true}
          label="Group by:"
          options={groupByDropdownOptions}
          value={groupByValue}
        />
      </div>
      {horizontalFilters}
      {renderContent()}
    </React.Fragment>
  )
}

export default SkillSection;
